import {
  packageShopKey,
  ProductTypeFunnels,
  type PackageType,
  type PackageTotalPrice,
  ChosenProduct,
} from "@b2bportal/core-packages";
import type { Funnel } from "@b2bportal/core-types";
import type { PriceQuoteV2 } from "@b2bportal/lodging-api";
import type { PackageQuote } from "@b2bportal/packages-api";
import { Product } from "@b2bportal/purchase-api";
import type { CartContext } from "@checkout/states";
import { ParentState } from "@checkout/types";
import type { State } from "xstate";
import type { PackageMachineContext } from "../types";

type PackageCheckoutState = State<PackageMachineContext>;
type PackageCheckoutStateWithoutValue = Pick<PackageCheckoutState, "context">;

export const getCurrentActivePackageType = (
  state: PackageCheckoutStateWithoutValue
): PackageType => state.context[packageShopKey].packageType;

export const getCurrentActivePackageToken = (
  state: PackageCheckoutStateWithoutValue
): string => state.context[packageShopKey].packageToken;

export const getCurrentActivePackageTotal = (
  state: PackageCheckoutStateWithoutValue
): PackageTotalPrice => state.context[packageShopKey].totalPrice;

/**
 * Returns the payNowTotal from the /complete response.
 * Used as the tripTotal base for flex-points remaining-balance calculation
 * in the package checkout (replacing the cart-quote balance which only
 * reflects taxes/fees and causes over-redemption).
 */
export const getPayNowTotalFromComplete = (
  state: PackageCheckoutStateWithoutValue
): PackageTotalPrice["payNowTotal"] =>
  state.context[packageShopKey]?.totalPrice?.payNowTotal;

/**
 * Returns payNowTotal from the quote breakdown when available (authoritative),
 * falling back to /complete data before the quote runs.
 */
export const getPackagePayNowTotal = (
  state: PackageCheckoutStateWithoutValue
): PackageTotalPrice["payNowTotal"] =>
  getQuotedPackageProduct(state)?.totalPackagePricing?.payNowTotal ??
  getPayNowTotalFromComplete(state);

export const getFunnelsNeededForPackage = (
  state: PackageCheckoutStateWithoutValue
): Funnel[] => {
  const packageType = getCurrentActivePackageType(state);
  return ProductTypeFunnels[packageType];
};

export const getSelectedDriver = (
  state: PackageCheckoutStateWithoutValue
): string | undefined => state.context[packageShopKey].selectedDriver;

export const getSelectedHotelBooker = (
  state: PackageCheckoutStateWithoutValue
): string | undefined => state.context[packageShopKey].selectedHotelBooker;

export const getChosenProducts = (state: PackageCheckoutStateWithoutValue) =>
  state.context[packageShopKey].chosenProducts;

export const getTotalPriceFromComplete = (
  state: PackageCheckoutStateWithoutValue
) => state.context[packageShopKey]?.totalPrice;

export const getQuotedPackageProduct = (
  state: Pick<State<CartContext>, "context">
): PackageQuote | undefined => {
  const cartQuote = state.context[ParentState.cartQuote];
  if (cartQuote == null) return undefined;

  const breakdown = cartQuote.quoteBreakdown;
  if (breakdown == null) return undefined;

  const packageProduct = breakdown.products?.find(
    (p) => p.product.type === Product.Package
  );
  return packageProduct?.product?.value;
};

export const getPackageBreakdownItems = (
  state: Pick<State<CartContext>, "context">
) => {
  const quotedPackageProduct = getQuotedPackageProduct(state);
  const quotedPricing = quotedPackageProduct?.totalPackagePricing;
  if (quotedPricing) {
    return {
      pricePerTravelerBeforeTaxesAndFees:
        quotedPackageProduct.perTravelerPricing.subtotal,
      tripTotal: quotedPricing.tripTotal,
      taxesAndFees: quotedPricing.taxesAndFeesTotal,
      payNowTotal: quotedPricing.payNowTotal,
      totalBeforeTaxesAndFees: quotedPricing.subtotal,
      payLaterTotal: quotedPricing.payLaterTotal,
    };
  }
  return undefined;
};

export const getHotelFromQuote = (
  state: Pick<State<CartContext>, "context">
): PriceQuoteV2 | undefined => {
  const cartQuote = state.context[ParentState.cartQuote];
  if (cartQuote == null) return undefined;

  const breakdown = cartQuote.quoteBreakdown;
  if (breakdown == null) return undefined;

  const hotelProduct = breakdown.products?.find(
    (p) => p.product.type === Product.Hotel
  );
  return hotelProduct?.product?.value;
};

export const getSelectedPassengerCount = (
  state: PackageCheckoutStateWithoutValue
): number => {
  const travelerInfo = state.context[ParentState.travelerInformation];
  const seated = travelerInfo?.selectedTravelerIds?.length ?? 0;
  const lapInfants = travelerInfo?.selectedLapInfantIds?.length ?? 0;
  return seated + lapInfants;
};

export const getSelectedLodging = (state: PackageCheckoutStateWithoutValue) =>
  state.context[packageShopKey].selectedLodging;

export const getSelectedCarCancellationPolicy = (
  state: PackageCheckoutStateWithoutValue
) => state.context[packageShopKey].selectedCarCancellationPolicy;

export const getDateRange = (state: PackageCheckoutStateWithoutValue) =>
  state.context[packageShopKey].fromDate &&
  state.context[packageShopKey].untilDate
    ? {
        from: state.context[packageShopKey].fromDate,
        until: state.context[packageShopKey].untilDate,
      }
    : undefined;

export const getPackageDestinationName = (
  state: PackageCheckoutStateWithoutValue
): string => {
  // First priority: Hotel city from selectedLodging
  const lodgingCity =
    state.context[packageShopKey].selectedLodging?.lodging?.city;
  if (lodgingCity) {
    return lodgingCity;
  }

  // Second priority: Flight destination from chosenProducts
  const chosenFlight = state.context[packageShopKey].chosenProducts?.find(
    (p) => p.ChosenProduct === ChosenProduct.ChosenFlight
  );
  if (chosenFlight && "returning" in chosenFlight) {
    // For round trips, the returning origin is the destination
    return chosenFlight.returning.origin;
  }

  // Fallback
  return "";
};
